import { ApiClient } from '@/config/axios.config';
import type { CargoItem, CreateCargoRequest, UpdateCargoRequest } from '@/types/cargoList.types';
import { mockCargoData } from '@/data/mockCargoData';
import { environment } from '@/config/environment';

export class CargoListService {
  private static readonly BASE_PATH = '/cargolists';

  /**
   * True once a list read has fallen back to `mockCargoData`. Non-production
   * only - in production the read throws instead. Read by the list page to warn
   * that the rows on screen are fabricated.
   */
  static usingMockData = false;

  /**
   * Fetch all cargo list items
   */
  static async getCargoList(): Promise<CargoItem[]> {
    try {
      const items = await ApiClient.get<CargoItem[]>(this.BASE_PATH);
      CargoListService.usingMockData = false;
      return items;
    } catch (error) {
      // Outside production, fall back to mock rows so the UI is still workable
      // against a dead API. This used to be silent, which meant a genuine
      // backend failure - a missing `Segment` column on the view, say - looked
      // like a working list of five cargoes. The banner makes it obvious that
      // what is on screen is not real data.
      if (!environment.production) {
        console.warn('API failed, using mock data:', error);
        CargoListService.usingMockData = true;
        return mockCargoData;
      }
      throw error;
    }
  }

  /**
   * Fetch a specific cargo item by ID.
   *
   * There is no `GET /cargolists/{id}` route - a single item is read from the
   * collection endpoint with an `id` query string.
   */
  static async getCargoItem(id: number): Promise<CargoItem> {
    return ApiClient.get<CargoItem>(`${this.BASE_PATH}?id=${id}`);
  }

  /**
   * Create a new cargo item. Responds 201 with `{ id }` - not the full entity.
   */
  static async createCargoItem(data: CreateCargoRequest): Promise<{ id: number }> {
    return ApiClient.post<{ id: number }>(this.BASE_PATH, data);
  }

  /**
   * Update an existing cargo item. Responds 204 No Content, so re-read the item
   * with `getCargoItem` if the updated entity is needed.
   */
  static async updateCargoItem(id: number, data: UpdateCargoRequest): Promise<void> {
    return ApiClient.put<void>(`${this.BASE_PATH}/${id}`, data);
  }

  /**
   * Delete a cargo item
   */
  static async deleteCargoItem(id: number): Promise<void> {
    return ApiClient.delete<void>(`${this.BASE_PATH}/${id}`);
  }
}
